// Pattern review pane for the recompute lane.
//
// A human works through every review item (diff group, attestation anomaly, or log template) and
// records an explicit accept/reject. verifyTestExecution refuses to certify the run until every
// item is accepted (see functions/src/testExecution/verifyTestExecution.ts). Acknowledgements are
// scoped to this execution and never carry across runs, so this is always a fresh review.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  List,
  ListItemButton,
  Pagination,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CheckCircle, Cancel, HelpOutline, OpenInNew, Lightbulb } from '@mui/icons-material';
import type { TestExecution, PatternAck } from '@finvari/deliver';
import type { AnalysisIndex, DiffMemberRef, ReviewItem } from '@finvari/test-api';
import { acknowledgePattern } from '../../../firebase';
import PatchViewer from './PatchViewer';
import {
  buildProgramDiffIndex,
  customerEntityUrl,
  fetchGroupMembers,
  fetchMemberPatch,
  isAccepted,
  sortReviewItems,
} from './patternAnalysis';

const MEMBERS_PER_PAGE = 20;

function itemLabel(item: ReviewItem): string {
  if (item.kind === 'diff') return item.label;
  if (item.kind === 'log') return item.template;
  return item.label;
}

function itemDocCount(item: ReviewItem): number {
  if (item.kind === 'diff') return item.docCount;
  if (item.kind === 'log') return item.count;
  return 0;
}

function itemPrograms(item: ReviewItem): string[] {
  if (item.kind === 'diff' || item.kind === 'log') return Object.keys(item.programs);
  return [item.programId];
}

function VerdictIcon({ ack }: { ack?: PatternAck }) {
  if (ack?.verdict === 'accepted') return <CheckCircle color="success" fontSize="small" />;
  if (ack?.verdict === 'rejected') return <Cancel color="error" fontSize="small" />;
  return <HelpOutline color="disabled" fontSize="small" />;
}

function KindChip({ item }: { item: ReviewItem }) {
  if (item.kind === 'attestation') {
    return <Chip label="attestation" size="small" color={item.severity === 'warn' ? 'warning' : 'default'} variant="outlined" />;
  }
  if (item.kind === 'log') {
    return <Chip label={`log · ${item.severity.toLowerCase()}`} size="small" color="warning" variant="outlined" />;
  }
  if (item.mechanical) {
    return <Chip label="mechanical" size="small" variant="outlined" />;
  }
  return <Chip label="change" size="small" color="info" variant="outlined" />;
}

interface MemberViewProps {
  executionId: string;
  programDiffIndex: Record<string, number>;
  member: DiffMemberRef;
}

// Lazily fetches and renders one member's patch. Kept small so the detail pane can page members.
function MemberPatch({ executionId, programDiffIndex, member }: MemberViewProps) {
  const [patch, setPatch] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPatch(null);
    setError(null);
    fetchMemberPatch(executionId, programDiffIndex, member)
      .then((text) => { if (!cancelled) setPatch(text); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [executionId, programDiffIndex, member]);

  if (error) return <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>;
  if (patch === null) return <Box sx={{ p: 2 }}><CircularProgress size={18} /></Box>;
  return (
    <Box sx={{ height: 360, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <PatchViewer content={patch} filename={member.patchFile || `${member.docId}.patch`} />
    </Box>
  );
}

interface PatternDetailProps {
  executionId: string;
  index: AnalysisIndex;
  analysisArtifactIndex: number;
  programDiffIndex: Record<string, number>;
  testEnv: string | null;
  baselineEnv: string | null;
  item: ReviewItem;
  ack?: PatternAck;
  onVerdict: (item: ReviewItem, verdict: 'accepted' | 'rejected', note?: string) => Promise<void>;
  busy: boolean;
}

function PatternDetail({
  executionId, index, analysisArtifactIndex, programDiffIndex, testEnv, baselineEnv, item, ack, onVerdict, busy,
}: PatternDetailProps) {
  const [members, setMembers] = useState<DiffMemberRef[] | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [note, setNote] = useState('');

  const prior = index.priorVerdicts?.[item.signature];

  useEffect(() => {
    setNote(ack?.note ?? '');
    setPage(1);
  }, [item.signature, ack?.note]);

  useEffect(() => {
    if (item.kind !== 'diff') { setMembers(null); return; }
    let cancelled = false;
    setMembers(null);
    setMembersError(null);
    fetchGroupMembers(executionId, analysisArtifactIndex, item.membersFile)
      .then((m) => { if (!cancelled) setMembers(m); })
      .catch((e) => { if (!cancelled) setMembersError(e.message); });
    return () => { cancelled = true; };
  }, [executionId, analysisArtifactIndex, item]);

  const pagedMembers = useMemo(() => {
    if (!members) return [];
    const start = (page - 1) * MEMBERS_PER_PAGE;
    return members.slice(start, start + MEMBERS_PER_PAGE);
  }, [members, page]);

  return (
    <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <VerdictIcon ack={ack} />
        <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>{itemLabel(item)}</Typography>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
        <KindChip item={item} />
        {item.kind === 'diff' && item.deltaClasses.map((c) => (
          <Chip key={c} label={c} size="small" variant="outlined" />
        ))}
        <Chip label={`${itemDocCount(item)} doc${itemDocCount(item) === 1 ? '' : 's'}`} size="small" />
        <Chip label={`${itemPrograms(item).length} program${itemPrograms(item).length === 1 ? '' : 's'}`} size="small" />
        {item.kind === 'diff' && item.highCardinality && (
          <Chip label="high cardinality" size="small" color="warning" />
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Programs: {itemPrograms(item).join(', ')}
      </Typography>

      {item.kind === 'diff' && item.remediationHint && (
        <Alert icon={<Lightbulb fontSize="inherit" />} severity="info" sx={{ mb: 1 }}>
          {item.remediationHint}
        </Alert>
      )}
      {item.kind === 'attestation' && (
        <Alert severity={item.severity === 'warn' ? 'warning' : 'info'} sx={{ mb: 1 }}>
          {item.detail}
        </Alert>
      )}
      {prior && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Prior run {prior.executionId} recorded <strong>{prior.verdict}</strong>
          {prior.note ? ` — "${prior.note}"` : ''}. This is a hint only; you must review afresh.
        </Alert>
      )}

      {item.kind === 'log' && (
        <Box sx={{ mb: 1 }}>
          {item.examples.map((ex, i) => (
            <Typography key={i} variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'action.hover', p: 1, borderRadius: 1, mb: 0.5 }}>
              {ex}
            </Typography>
          ))}
        </Box>
      )}

      {item.kind === 'diff' && (
        <>
          <Divider sx={{ my: 1 }} />
          {membersError && <Alert severity="error">{membersError}</Alert>}
          {members === null && !membersError && <CircularProgress size={18} />}
          {members && members.length > MEMBERS_PER_PAGE && (
            <Pagination
              size="small"
              count={Math.ceil(members.length / MEMBERS_PER_PAGE)}
              page={page}
              onChange={(_, p) => setPage(p)}
              sx={{ mb: 1 }}
            />
          )}
          <Stack spacing={2}>
            {pagedMembers.map((m) => (
              <Box key={`${m.programId}/${m.docId}`}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={600}>{m.programId}</Typography>
                  <Typography variant="caption" color="text.secondary">{m.docId}</Typography>
                  {customerEntityUrl(baselineEnv, m.programId, m.entity, m.docId) && (
                    <Link href={customerEntityUrl(baselineEnv, m.programId, m.entity, m.docId)!} target="_blank" rel="noopener" variant="caption">
                      Open in baseline <OpenInNew sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                    </Link>
                  )}
                  {customerEntityUrl(testEnv, m.programId, m.entity, m.docId) && (
                    <Link href={customerEntityUrl(testEnv, m.programId, m.entity, m.docId)!} target="_blank" rel="noopener" variant="caption">
                      Open in test env <OpenInNew sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                    </Link>
                  )}
                </Stack>
                <MemberPatch executionId={executionId} programDiffIndex={programDiffIndex} member={m} />
              </Box>
            ))}
          </Stack>
        </>
      )}

      <Divider sx={{ my: 2 }} />
      <TextField
        label="Note (optional; required to reject)"
        size="small"
        fullWidth
        multiline
        minRows={1}
        maxRows={4}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        sx={{ mb: 1 }}
      />
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          color="success"
          startIcon={busy ? <CircularProgress size={16} /> : <CheckCircle />}
          disabled={busy}
          onClick={() => onVerdict(item, 'accepted', note.trim() || undefined)}
        >
          Accept
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<Cancel />}
          disabled={busy || !note.trim()}
          onClick={() => onVerdict(item, 'rejected', note.trim())}
        >
          Reject
        </Button>
      </Stack>
    </Box>
  );
}

interface PatternReviewProps {
  executionId: string;
  testExecution: TestExecution;
  index: AnalysisIndex;
  analysisArtifactIndex: number;
  acks: Record<string, PatternAck>;
}

export default function PatternReview({
  executionId, testExecution, index, analysisArtifactIndex, acks,
}: PatternReviewProps) {
  const programDiffIndex = useMemo(() => buildProgramDiffIndex(testExecution), [testExecution]);
  const sorted = useMemo(() => sortReviewItems(index.items, acks), [index.items, acks]);
  const [selectedSig, setSelectedSig] = useState<string | null>(sorted[0]?.signature ?? null);
  const [busySig, setBusySig] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const selected = sorted.find((i) => i.signature === selectedSig) ?? sorted[0] ?? null;
  const selectedIndex = sorted.findIndex((i) => i.signature === selected?.signature);

  const move = useCallback((delta: number) => {
    if (sorted.length === 0) return;
    const next = Math.max(0, Math.min(sorted.length - 1, selectedIndex + delta));
    setSelectedSig(sorted[next].signature);
  }, [sorted, selectedIndex]);

  const handleVerdict = useCallback(async (item: ReviewItem, verdict: 'accepted' | 'rejected', note?: string) => {
    setBusySig(item.signature);
    setError(null);
    try {
      await acknowledgePattern(executionId, item.signature, verdict, note);
      // On accept, advance to the next unresolved item to keep the reviewer moving.
      if (verdict === 'accepted') move(1);
    } catch (e: any) {
      setError(e.message || 'Failed to record verdict');
    } finally {
      setBusySig(null);
    }
  }, [executionId, move]);

  // Keyboard nav: j/k or arrow up/down to move between items.
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
  }, [move]);

  if (index.items.length === 0) {
    return <Alert severity="success">No review items — the recompute produced no diffs, log warnings, or attestation anomalies.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', height: 640, border: 1, borderColor: 'divider', borderRadius: 1 }} onKeyDown={onKeyDown} tabIndex={0}>
      <Box sx={{ width: 360, borderRight: 1, borderColor: 'divider', overflow: 'auto' }}>
        <List ref={listRef} dense disablePadding>
          {sorted.map((item) => {
            const ack = acks[item.signature];
            return (
              <ListItemButton
                key={item.signature}
                selected={item.signature === selected?.signature}
                onClick={() => setSelectedSig(item.signature)}
                sx={{ alignItems: 'flex-start', gap: 1 }}
              >
                <Box sx={{ pt: 0.5 }}><VerdictIcon ack={ack} /></Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {itemLabel(item)}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <KindChip item={item} />
                    <Chip label={`${itemDocCount(item)}`} size="small" />
                  </Stack>
                </Box>
              </ListItemButton>
            );
          })}
        </List>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        <Stack direction="row" spacing={1} sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }} alignItems="center">
          <Tooltip title="Previous (k / ↑)"><span><IconButton size="small" onClick={() => move(-1)} disabled={selectedIndex <= 0}>▲</IconButton></span></Tooltip>
          <Tooltip title="Next (j / ↓)"><span><IconButton size="small" onClick={() => move(1)} disabled={selectedIndex >= sorted.length - 1}>▼</IconButton></span></Tooltip>
          <Typography variant="caption" color="text.secondary">
            {selectedIndex + 1} of {sorted.length}
          </Typography>
        </Stack>
        {selected && (
          <PatternDetail
            executionId={executionId}
            index={index}
            analysisArtifactIndex={analysisArtifactIndex}
            programDiffIndex={programDiffIndex}
            testEnv={testExecution.environment ?? null}
            baselineEnv={index.baseline.environment}
            item={selected}
            ack={acks[selected.signature]}
            onVerdict={handleVerdict}
            busy={busySig === selected.signature}
          />
        )}
      </Box>
    </Box>
  );
}

export { isAccepted };
